import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Switch from "@mui/material/Switch";
import styles from "./styles.module.scss";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, {
  RowWithOptionalClick,
} from "../../../../../components/table";
import Pagination from "../../../../../components/pagination";
import {
  getOrganizationClientAudit,
  getOrganizationClients,
  setOrganizationClientAutoAcceptConsent,
  setOrganizationClientEnabled,
} from "../../../../../setup/api/services/organization";
import {
  IOrgClient,
  IOrgClientAudit,
  OrgClientAuditActorType,
  OrgClientAuditEventType,
} from "../../../../../setup/api/services/organization/models";
import ReasonPromptModal from "./ReasonPromptModal";

interface ClientsInformationProps {
  organization: IOrganizations | undefined;
}

/**
 * BusinessTable's default export is React.memo(...)-wrapped, which erases its generic type
 * parameter — so the row type must nominally extend RowWithOptionalClick (matching
 * UsersInformation's identical pattern) rather than relying on structural compatibility alone;
 * TS's "weak type" check otherwise rejects passing IOrgClient[]/IOrgClientAudit[] directly.
 */
interface ClientRow extends RowWithOptionalClick, IOrgClient {}
interface AuditRow extends RowWithOptionalClick, IOrgClientAudit {}

/**
 * Staff-only client-management view (design doc §9 Phase 2): every KC client for this org —
 * self-service or staff-added — with enable/disable and auto-accept-consent control, plus the
 * audit trail those controls (and self-service creation) write to.
 */
const ClientsInformation = ({ organization }: ClientsInformationProps) => {
  const { t } = useTranslation();

  const EVENT_LABELS: Record<OrgClientAuditEventType, string> = {
    CREATED: t("lbl_audit_event_created"),
    ENABLED: t("lbl_audit_event_enabled"),
    DISABLED: t("lbl_audit_event_disabled"),
    AUTO_ACCEPT_CONSENT_CHANGED: t(
      "lbl_audit_event_auto_accept_consent_changed"
    ),
  };

  const ACTOR_LABELS: Record<OrgClientAuditActorType, string> = {
    SELF_SERVICE: t("lbl_audit_actor_self_service"),
    STAFF: t("lbl_audit_actor_staff"),
  };

  const clientColumns = [
    { type: "clientId", value: t("lbl_client_id") },
    { type: "description", value: t("description") },
    { type: "status", value: t("lbl_status") },
    { type: "consent", value: t("lbl_auto_accept_consent") },
  ];

  const auditColumns = [
    { type: "clientId", value: t("lbl_audit_client") },
    { type: "eventType", value: t("lbl_audit_event") },
    { type: "actorType", value: t("lbl_audit_actor") },
    { type: "reason", value: t("lbl_audit_reason") },
    { type: "createdAt", value: t("lbl_audit_created_at") },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [savingClientId, setSavingClientId] = useState<string | null>(null);

  const [pendingConsentClientId, setPendingConsentClientId] = useState<
    string | null
  >(null);
  const [pendingConsentTarget, setPendingConsentTarget] = useState(false);

  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPagesCount, setAuditPagesCount] = useState(1);
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const auditPageSize = 10;

  const fetchClients = useCallback(async () => {
    if (!organization?.code) return;
    setIsLoading(true);
    try {
      const response = await getOrganizationClients(organization.code);
      setClients(response?.data ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [organization?.code]);

  const fetchAudit = useCallback(async () => {
    if (!organization?.code) return;
    setAuditLoading(true);
    try {
      const response = await getOrganizationClientAudit(
        organization.code,
        auditPage - 1,
        auditPageSize
      );
      setAuditRows(response?.data?.content ?? []);
      setAuditPagesCount(response?.data?.totalPages ?? 1);
      setAuditTotalCount(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error(error);
    } finally {
      setAuditLoading(false);
    }
  }, [organization?.code, auditPage]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const handleToggleEnabled = async (
    clientId: string,
    nextEnabled: boolean
  ) => {
    if (!organization?.code) return;
    setSavingClientId(clientId);
    try {
      await setOrganizationClientEnabled(
        organization.code,
        clientId,
        nextEnabled
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, enabled: nextEnabled } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const handleToggleConsentRequested = (
    clientId: string,
    nextValue: boolean
  ) => {
    setPendingConsentClientId(clientId);
    setPendingConsentTarget(nextValue);
  };

  const handleConfirmConsentChange = async (reason: string) => {
    if (!organization?.code || !pendingConsentClientId) return;
    const clientId = pendingConsentClientId;
    const nextValue = pendingConsentTarget;
    setPendingConsentClientId(null);
    setSavingClientId(clientId);
    try {
      await setOrganizationClientAutoAcceptConsent(
        organization.code,
        clientId,
        nextValue,
        reason
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, autoAcceptConsent: nextValue } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const renderCell = (columnType: string, row: ClientRow) => {
    switch (columnType) {
      case "clientId":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.clientId}
          </span>
        );
      case "description":
        return (
          <span className={styles.nameValue}>{row.description || "—"}</span>
        );
      case "status":
        return (
          <Switch
            checked={row.enabled}
            disabled={savingClientId === row.clientId}
            onChange={(_, checked) =>
              handleToggleEnabled(row.clientId, checked)
            }
          />
        );
      case "consent":
        return (
          <Switch
            checked={row.autoAcceptConsent}
            disabled={savingClientId === row.clientId}
            onChange={(_, checked) =>
              handleToggleConsentRequested(row.clientId, checked)
            }
          />
        );
      default:
        return null;
    }
  };

  const renderAuditCell = (columnType: string, row: AuditRow) => {
    switch (columnType) {
      case "clientId":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.clientId}
          </span>
        );
      case "eventType":
        return (
          <span className={styles.nameValue}>
            {EVENT_LABELS[row.eventType] ?? row.eventType}
          </span>
        );
      case "actorType":
        return (
          <span className={styles.nameValue}>
            {ACTOR_LABELS[row.actorType] ?? row.actorType}
          </span>
        );
      case "reason":
        return <span className={styles.nameValue}>{row.reason || "—"}</span>;
      case "createdAt":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {new Date(row.createdAt).toLocaleString("ar-SA")}
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h3 className={styles.title}>{t("lbl_api_clients")}</h3>
        <p className={styles.subtitle}>{t("lbl_api_clients_subtitle")}</p>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.clientsTableWrapper}>
          <BusinessTable
            columns={clientColumns}
            rows={clients}
            renderCell={(column, row) => renderCell(column, row as ClientRow)}
          />
        </div>
      </div>

      <div className={styles.auditSection}>
        <h4 className={styles.auditTitle}>{t("lbl_audit_log")}</h4>
        {auditLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        ) : (
          <>
            <div className={styles.auditTableWrapper}>
              <BusinessTable
                columns={auditColumns}
                rows={auditRows}
                renderCell={(column, row) =>
                  renderAuditCell(column, row as AuditRow)
                }
              />
            </div>
            <Pagination
              countName={t("lbl_audit_log_row_name")}
              pagesCount={auditPagesCount}
              page={auditPage}
              pageSize={auditPageSize}
              length={auditTotalCount}
              handlePaginationChange={(page) => setAuditPage(page)}
            />
          </>
        )}
      </div>

      <ReasonPromptModal
        open={!!pendingConsentClientId}
        title={
          pendingConsentTarget
            ? t("lbl_grant_auto_accept_consent_title")
            : t("lbl_revoke_auto_accept_consent_title")
        }
        handleClose={() => setPendingConsentClientId(null)}
        handleConfirm={handleConfirmConsentChange}
      />
    </div>
  );
};

export default ClientsInformation;
